<template>
    <a-spin tip="Carregando..." :spinning="loading">
        <div class="container">
            <a-card>
                <template #title>
                    Logs de Acesso
                </template>

                <!-- Filtros principais -->
                <a-row :gutter="16" style="margin-bottom: 12px;">
                    <a-col :span="5">
                        <a-input
                            v-model:value="filters.user"
                            placeholder="Usuário, ID ou e-mail"
                            allow-clear
                            @input="onFiltersChanged"
                        />
                    </a-col>

                    <a-col :span="4">
                        <a-input
                            v-model:value="filters.ip"
                            placeholder="IP"
                            allow-clear
                            @input="onFiltersChanged"
                        />
                    </a-col>

                    <!-- Mais espaço para o tipo de log -->
                    <a-col :span="8">
                        <a-select
                            v-model:value="filters.log_type"
                            :options="logTypeOptions"
                            placeholder="Tipo de Log"
                            allow-clear
                            style="width: 100%;"
                            @change="onFiltersChanged"
                        />
                    </a-col>

                    <a-col :span="3">
                        <a-select
                            v-model:value="filters.application"
                            :options="applicationOptions"
                            placeholder="Aplicação"
                            allow-clear
                            style="width: 100%;"
                            @change="onFiltersChanged"
                        />
                    </a-col>

                    <a-col :span="4">
                        <a-select
                            v-model:value="filters.origin"
                            :options="originOptions"
                            placeholder="Origem"
                            allow-clear
                            style="width: 100%;"
                            @change="onFiltersChanged"
                        />
                    </a-col>
                </a-row>

                <!-- Filtro de período em outra linha, com mais espaço -->
                <a-row :gutter="16" style="margin-bottom: 16px;">
                    <a-col :span="10">
                        <div style="display:flex; flex-direction:column; gap:4px;">
                            <span style="font-size: 12px; color:#666;">Período</span>
                            <a-range-picker
                                v-model:value="filters.dateRangePicker"
                                :presets="datePresets"
                                style="width: 100%;"
                                :placeholder="['Data inicial', 'Data final']"
                                format="DD/MM/YYYY"
                                value-format="YYYY-MM-DD"
                            />
                        </div>
                    </a-col>

                    <a-col :span="4" style="display:flex; align-items:flex-end;">
                        <a-button
                            type="primary"
                            :disabled="!canFilterByDate"
                            @click="applyDateFilter"
                            style="margin-right: 8px; width: 100%;"
                        >
                            Filtrar período
                        </a-button>
                    </a-col>

                    <a-col :span="4" style="display:flex; align-items:flex-end;">
                        <a-button
                            @click="clearDateFilter"
                            style="width: 100%;"
                        >
                            Limpar período
                        </a-button>
                    </a-col>
                </a-row>

                <!-- Tabela -->
                <a-table
                    :columns="columns"
                    :data-source="logs"
                    :pagination="pagination"
                    :row-key="record => record.id"
                    @change="handleTableChange"
                    bordered
                >
                    <template #bodyCell="{ column, record }">
                        <!-- Usuário -->
                        <template v-if="column.dataIndex === 'user'">
                            <span>{{ record.user?.name ?? record.user_id ?? '-' }}</span>
                        </template>

                        <!-- E-mail -->
                        <template v-else-if="column.dataIndex === 'email'">
                            <span>{{ record.user?.email ?? '-' }}</span>
                        </template>

                        <!-- Status amigável -->
                        <template v-else-if="column.dataIndex === 'log_type'">
                            <a-tag :color="statusColor(record.log_type)">
                                {{ statusLabel(record.log_type) }}
                            </a-tag>
                        </template>

                        <!-- Origem amigável -->
                        <template v-else-if="column.dataIndex === 'origin'">
                            <span>{{ originLabel(record.origin) }}</span>
                        </template>

                        <!-- Aplicação amigável -->
                        <template v-else-if="column.dataIndex === 'application'">
                            <span>{{ applicationLabel(record.application) }}</span>
                        </template>

                        <!-- Data/Hora formatada -->
                        <template v-else-if="column.dataIndex === 'created_at'">
                            <span>{{ formatDateTime(record.created_at) }}</span>
                        </template>

                        <!-- Ações (ícone do mapa) -->
                        <template v-else-if="column.dataIndex === 'actions'">
                            <template v-if="hasCoordinates(record.ip_coordinates)">
                                <a-tooltip title="Ver localização no Google Maps">
                                    <a-button
                                        type="link"
                                        size="small"
                                        @click="openInMaps(record.ip_coordinates)"
                                    >
                                        <i class="fa-solid fa-location-dot"></i>
                                    </a-button>
                                </a-tooltip>
                            </template>
                            <template v-else>
                                <span>-</span>
                            </template>
                        </template>

                        <!-- Demais campos -->
                        <template v-else>
                            <span>{{ record[column.dataIndex] }}</span>
                        </template>
                    </template>
                </a-table>
            </a-card>
        </div>
    </a-spin>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { notification } from 'ant-design-vue'
import dayjs from 'dayjs'

const loading = ref(false)

// lista exibida na tabela (página atual)
const logs = ref([])

// filtros
const filters = reactive({
    user: '',
    ip: '',
    log_type: null,
    application: null,
    origin: null,
    // o que o usuário seleciona no calendário
    dateRangePicker: [],
    // o que realmente está aplicado no filtro
    dateRange: [],
})

// presets de datas (mais intuitivo)
const datePresets = [
    {
        label: 'Hoje',
        value: [dayjs().startOf('day'), dayjs().endOf('day')],
    },
    {
        label: 'Últimos 7 dias',
        value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
    },
    {
        label: 'Últimos 30 dias',
        value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')],
    },
]

// botão de filtrar período só habilita se tiver 2 datas
const canFilterByDate = computed(() => {
    return (
        Array.isArray(filters.dateRangePicker) &&
        filters.dateRangePicker.length === 2 &&
        filters.dateRangePicker[0] &&
        filters.dateRangePicker[1]
    )
})

const logTypeOptions = [
    { label: 'Login bem-sucedido', value: 'success_login' },
    { label: 'Falha no login - senha incorreta', value: 'failed_login_incorrect_pass' },
    { label: 'Falha no login - e-mail incorreto', value: 'failed_login_incorrect_email' },
    { label: 'Logout', value: 'logout' },
]

const applicationOptions = [
    { label: 'Desktop', value: 'desktop' },
    { label: 'Mobile', value: 'mobile' },
]

const originOptions = [
    { label: 'Login Interno', value: 'internal-login' },
    { label: 'Login Externo', value: 'external-login' },
]

const pagination = reactive({
    current: 1,
    pageSize: 20,
    total: 0,
    showTotal: (total) => `Total de ${total} registros`,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
})

const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Usuário', dataIndex: 'user', key: 'user', width: 160 },
    { title: 'E-mail', dataIndex: 'email', key: 'email', width: 200 },
    { title: 'IP', dataIndex: 'ip', key: 'ip', width: 140 },
    { title: 'Origem', dataIndex: 'origin', key: 'origin', width: 140 },
    { title: 'Status', dataIndex: 'log_type', key: 'log_type', width: 190 },
    { title: 'Aplicação', dataIndex: 'application', key: 'application', width: 120 },
    { title: 'Data/Hora', dataIndex: 'created_at', key: 'created_at', width: 180 },
    { title: 'Ações', dataIndex: 'actions', key: 'actions', width: 80 },
]

const statusLabel = (type) => {
    switch (type) {
        case 'success_login':
            return 'Login bem-sucedido'
        case 'failed_login_incorrect_pass':
            return 'Falha no login - senha incorreta'
        case 'failed_login_incorrect_email':
            return 'Falha no login - e-mail incorreto'
        case 'logout':
            return 'Logout'
        default:
            return type || '-'
    }
}

const statusColor = (type) => {
    switch (type) {
        case 'success_login':
            return 'green'
        case 'logout':
            return 'blue'
        case 'failed_login_incorrect_pass':
        case 'failed_login_incorrect_email':
            return 'red'
        default:
            return 'default'
    }
}

const applicationLabel = (app) => {
    switch (app) {
        case 'desktop':
            return 'Desktop'
        case 'mobile':
            return 'Mobile'
        default:
            return '-'
    }
}

const originLabel = (origin) => {
    switch (origin) {
        case 'internal-login':
            return 'Login Interno'
        case 'external-login':
            return 'Login Externo'
        default:
            return origin || '-'
    }
}

const parseCoordinates = (value) => {
    if (!value) return null

    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value
        if (parsed && parsed.lat && parsed.lon) {
            return { lat: parsed.lat, lon: parsed.lon }
        }
    } catch (e) {
        if (typeof value === 'string' && value.includes(',')) {
            const [lat, lon] = value.split(',').map(v => v.trim())
            if (lat && lon) {
                return { lat, lon }
            }
        }
    }

    return null
}

const hasCoordinates = (value) => {
    return !!parseCoordinates(value)
}

const openInMaps = (value) => {
    const coords = parseCoordinates(value)
    if (!coords) return
    const url = `https://www.google.com/maps?q=${coords.lat},${coords.lon}`
    window.open(url, '_blank')
}

// Data formatada bonitinha
const formatDateTime = (value) => {
    if (!value) return '-'
    const d = dayjs(value)
    if (!d.isValid()) return value
    return d.format('DD/MM/YYYY HH:mm:ss')
}

const onFiltersChanged = () => {
    pagination.current = 1
    fetchLogs()
}

const applyDateFilter = () => {
    if (!canFilterByDate.value) return
    filters.dateRange = [...filters.dateRangePicker]
    pagination.current = 1
    fetchLogs()
}

const clearDateFilter = () => {
    filters.dateRangePicker = []
    filters.dateRange = []
    pagination.current = 1
    fetchLogs()
}

const handleTableChange = (pag) => {
    pagination.current = pag.current
    pagination.pageSize = pag.pageSize
    fetchLogs()
}

const fetchLogs = async () => {
    try {
        loading.value = true

        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
        }

        if (filters.user) {
            params.user = filters.user
        }

        if (filters.ip) {
            params.ip = filters.ip
        }

        if (filters.log_type) {
            params.log_type = filters.log_type
        }

        if (filters.application) {
            params.application = filters.application
        }

        if (filters.origin) {
            params.origin = filters.origin
        }

        if (
            Array.isArray(filters.dateRange) &&
            filters.dateRange.length === 2 &&
            filters.dateRange[0] &&
            filters.dateRange[1]
        ) {
            params.date_start = filters.dateRange[0]
            params.date_end = filters.dateRange[1]
        }

        const { data } = await window.axios.get('api/configurations/access-logs', { params })

        logs.value = data.data ?? []

        if (data.pagination) {
            pagination.total = data.pagination.total
            pagination.current = data.pagination.current_page
            pagination.pageSize = data.pagination.per_page
        }
    } catch (error) {
        notification.error({
            message: 'Erro ao carregar logs',
            description: error.message,
        })
    } finally {
        loading.value = false
    }
}

onMounted(fetchLogs)
</script>

<style scoped>
.container {
    padding: 16px;
}
</style>
